import { API, Auth } from "aws-amplify";
import { Formik } from "formik";
import Button from "_shared/components/Button/Button";
import CustomModal from "_shared/components/Modal/Modal";
import { z, ZodString } from "zod";
import InputField from "../../../../_shared/components/InputField/InputField";
import { useModal } from "../../../../_shared/contexts/ModalContext";
import { ProfileForm } from "../../models/ProfileForm";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  reloadUser: () => Promise<void>;
}

const ProfileModal: React.FC<Props & ProfileForm> = ({
  isOpen,
  onClose,
  reloadUser,
  label,
  fields,
  customInputs,
  customOnSubmit,
  customSchema,
}) => {
  const { onErrorMsg } = useModal();

  // initial empty string value for all fields
  const initialValues = fields.reduce<Record<string, string>>(
    (builtObj, field) => ({
      ...builtObj,
      [field.name]: field.initialValue ?? "",
    }),
    {},
  );

  const schema =
    customSchema ??
    z.object({
      ...fields.reduce<Record<string, ZodString>>(
        (builtObj, field) => ({
          ...builtObj,
          [field.name]: z
            .string()
            .min(1, { message: `${field.label} is required.` }),
        }),
        {},
      ),
    });

  const updateAttributes = async (attributes: Record<string, string>) => {
    try {
      const userInfo = await Auth.currentAuthenticatedUser();
      await Auth.updateUserAttributes(userInfo, attributes);
      // unverify sms when changing phone number
      if (Object.keys(attributes).includes("custom:phoneNumber")) {
        await API.post("auth", "/cognito/unverifySms", {});
      }
      await reloadUser();
      onClose();
    } catch (err: any) {
      onErrorMsg(err.message);
    }
  };

  return (
    <CustomModal
      open={isOpen}
      onClose={() => onClose()}
      title={`Edit ${label}`}
    >
      <Formik
        initialValues={initialValues}
        onSubmit={async (values) => {
          const result = schema.safeParse(values);
          if (!result.success) {
            onClose();
            onErrorMsg(result.error.issues[0].message);
            return;
          }
          if (customOnSubmit) {
            await customOnSubmit(values);
          } else {
            await updateAttributes(values);
          }
          // always reload user details
          reloadUser();
          onClose();
        }}
      >
        {({ handleSubmit, isSubmitting }) => (
          <form onSubmit={handleSubmit}>
            <div>
              {customInputs ??
                fields!.map((field) => (
                  // TODO: add placeholder of original value split
                  <InputField
                    dark
                    key={field.name}
                    name={field.name}
                    placeholder={field.placeholder}
                  />
                ))}
            </div>
            <div className="footer-buttons">
              <Button onClick={onClose} variant="secondary">
                Cancel
              </Button>
              <Button type="submit" loading={isSubmitting}>
                Save
              </Button>
            </div>
          </form>
        )}
      </Formik>
    </CustomModal>
  );
};

export default ProfileModal;
